// =============================================================================
// ATMOSPHERE BACKGROUND COMPONENT
// =============================================================================
// Manages a fixed fullscreen multi-image background stack that crossfades
// between authentic interior views as the user drills down / scrolls through
// sections (#hero, #menu, #about, #gallery, #contact).
// =============================================================================

import React, { useEffect, useState, useRef } from 'react';

export interface AtmosphereLayer {
  /** Section ID (without #) that triggers this background image */
  sectionId: string;
  /** Image URL */
  src: string;
  /** Accessible description */
  alt?: string;
}

export interface AtmosphereBackgroundProps {
  /** Array of background image layers */
  layers: AtmosphereLayer[];
  /** Optional custom class name */
  className?: string;
}

export function AtmosphereBackground({ layers, className = '' }: AtmosphereBackgroundProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const observerRef = useRef<IntersectionObserver | null>(null);

  useEffect(() => {
    if (typeof window === 'undefined' || !layers.length) return;

    // Observe each section in the viewport
    const handleIntersect: IntersectionObserverCallback = (entries) => {
      // Find the entry that has the highest intersection ratio
      let bestEntry: IntersectionObserverEntry | null = null;
      let highestRatio = -1;

      for (const entry of entries) {
        if (entry.isIntersecting && entry.intersectionRatio > highestRatio) {
          highestRatio = entry.intersectionRatio;
          bestEntry = entry;
        }
      }

      if (bestEntry) {
        const id = bestEntry.target.id;
        const targetIndex = layers.findIndex((l) => l.sectionId === id);
        if (targetIndex !== -1) {
          setActiveIndex(targetIndex);
        }
      }
    };

    const observer = new IntersectionObserver(handleIntersect, {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: [0.1, 0.3, 0.5, 0.7, 0.9],
    });
    observerRef.current = observer;

    // Attach observer to section elements
    for (const layer of layers) {
      const el = document.getElementById(layer.sectionId);
      if (el) {
        observer.observe(el);
      }
    }

    // Scroll fallback listener in case sections are taller than viewport
    const handleScroll = () => {
      const scrollPosition = window.scrollY + window.innerHeight * 0.4;
      for (let i = layers.length - 1; i >= 0; i--) {
        const layer = layers[i];
        if (!layer) continue;
        const el = document.getElementById(layer.sectionId);
        if (el) {
          const rect = el.getBoundingClientRect();
          const elementTop = rect.top + window.scrollY;
          if (scrollPosition >= elementTop) {
            setActiveIndex(i);
            break;
          }
        }
      }
    };

    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', handleScroll);
    };
  }, [layers]);

  if (!layers.length) return null;

  return (
    <div
      className={`atmosphere-container ${className}`.trim()}
      aria-hidden="true"
      role="presentation"
    >
      <div className="atmosphere-image-wrapper">
        {layers.map((layer, index) => {
          const isActive = index === activeIndex;
          return (
            <img
              key={`${layer.sectionId}-${index}`}
              src={layer.src}
              alt={layer.alt ?? 'Restaurant atmosphere'}
              className={`atmosphere-layer ${isActive ? 'is-active' : ''}`}
              loading={index <= 1 ? 'eager' : 'lazy'}
              decoding="async"
            />
          );
        })}
      </div>
      <div className="atmosphere-overlay" />
    </div>
  );
}
